import React, { useState, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import HowToVoteRoundedIcon from "@mui/icons-material/HowToVoteRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import PollOutlinedIcon from "@mui/icons-material/PollOutlined";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import SortRoundedIcon from "@mui/icons-material/SortRounded";
import { CircularProgress } from "@mui/material";
import { getAllVotesWithFilters } from "../../../api/Vote";
import { getToken, getUserId } from "../../../api/index";
import "./AttachmentPickers.css";

const SORT_OPTIONS = [
  { value: "newest", label: "Newest First", icon: "🕒" },
  { value: "oldest", label: "Oldest First", icon: "⏳" },
  { value: "most_votes", label: "Most Votes", icon: "🔥" },
  { value: "least_votes", label: "Least Votes", icon: "📉" },
  { value: "title_asc", label: "Title (A – Z)", icon: "🔤" },
  { value: "title_desc", label: "Title (Z – A)", icon: "🔤" },
];

export default function BallotPickerModal({ open, onClose, onSelectBallot, selectedBallotId }) {
  const [ballots, setBallots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState("all"); // 'all' | 'my'
  const [sortBy, setSortBy] = useState("newest"); // 'newest' | 'oldest' | 'most_votes' | 'least_votes' | 'title_asc' | 'title_desc'
  const [sortOpen, setSortOpen] = useState(false);
  const [selectedBallot, setSelectedBallot] = useState(null);
  const [expandedBallotIds, setExpandedBallotIds] = useState(new Set());

  const sortRef = useRef(null);
  const currentUserId = getUserId();
  const token = getToken();

  // Close sort menu on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sortRef.current && !sortRef.current.contains(event.target)) {
        setSortOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (open) {
      fetchBallots();
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prevOverflow;
      };
    }
  }, [open]);

  // Sync selectedBallot with selectedBallotId when ballots load or modal opens
  useEffect(() => {
    if (selectedBallotId && ballots.length > 0) {
      const match = ballots.find((b) => String(b.voteId || b.id) === String(selectedBallotId));
      if (match) setSelectedBallot(match);
    } else if (!selectedBallotId) {
      setSelectedBallot(null);
    }
  }, [selectedBallotId, ballots, open]);

  const fetchBallots = async () => {
    try {
      setLoading(true);
      const params = {
        userId: currentUserId || undefined,
        skip: 0,
        take: 50,
      };
      const data = await getAllVotesWithFilters(params, token);
      if (Array.isArray(data)) {
        setBallots(data);
      } else if (data?.data && Array.isArray(data.data)) {
        setBallots(data.data);
      } else {
        setBallots([]);
      }
    } catch (err) {
      console.error("Error loading ballots for picker:", err);
      setBallots([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredBallots = useMemo(() => {
    const list = ballots.filter((b) => {
      const title = (b.title || "").toLowerCase();
      const term = searchTerm.toLowerCase().trim();

      const matchesSearch = !term || title.includes(term);

      if (!matchesSearch) return false;

      if (activeFilter === "my") {
        if (!currentUserId) return false;
        const creatorId = b.createdByUser || b.userId || "";
        return String(creatorId).toLowerCase() === String(currentUserId).toLowerCase();
      }

      return true;
    });

    return list.sort((a, b) => {
      const getVotes = (item) => {
        const opts = item.options || item.votingOption || [];
        return opts.reduce((sum, opt) => sum + (Number(opt.voteCount) || 0), 0) || Number(item.totalVotes) || Number(item.totalUserResponse) || 0;
      };

      const getDate = (item) => {
        const d = item.createdOn || item.createdDate || item.createdAt || item.creationDate || 0;
        return new Date(d).getTime() || 0;
      };

      if (sortBy === "newest") {
        return getDate(b) - getDate(a);
      }
      if (sortBy === "oldest") {
        return getDate(a) - getDate(b);
      }
      if (sortBy === "most_votes") {
        return getVotes(b) - getVotes(a);
      }
      if (sortBy === "least_votes") {
        return getVotes(a) - getVotes(b);
      }
      if (sortBy === "title_asc") {
        return (a.title || "").localeCompare(b.title || "");
      }
      if (sortBy === "title_desc") {
        return (b.title || "").localeCompare(a.title || "");
      }
      return 0;
    });
  }, [ballots, searchTerm, activeFilter, sortBy, currentUserId]);

  const handleCardClick = (ballot) => {
    setSelectedBallot(ballot);
  };

  const toggleExpand = (e, bId) => {
    e.stopPropagation();
    setExpandedBallotIds((prev) => {
      const next = new Set(prev);
      if (next.has(bId)) {
        next.delete(bId);
      } else {
        next.add(bId);
      }
      return next;
    });
  };

  const handleConfirmSelection = () => {
    if (selectedBallot) {
      onSelectBallot(selectedBallot);
      onClose();
    }
  };

  if (!open) return null;

  return createPortal(
    <AnimatePresence>
      <div className="picker-modal-overlay" onClick={onClose}>
        <motion.div
          className="picker-modal-container"
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 15 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="picker-modal-header">
            <div className="picker-header-title-box">
              <div className="picker-icon-badge ballot-badge">
                <HowToVoteRoundedIcon style={{ fontSize: 22, color: "#48D1CC" }} />
              </div>
              <div>
                <h3 className="picker-title">Attach a Ballot</h3>
                <p className="picker-subtitle">Select an active ballot or survey to embed with your discussion</p>
              </div>
            </div>
            <button className="btn-picker-close" onClick={onClose} title="Close">
              <CloseIcon style={{ fontSize: 20 }} />
            </button>
          </div>

          {/* Search, Filter Tabs & Sort Controls */}
          <div className="picker-controls-row">
            <div className="picker-search-wrapper">
              <SearchIcon className="picker-search-icon" />
              <input
                type="text"
                placeholder="Search ballots by title..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="picker-search-input"
                autoFocus
              />
              {searchTerm && (
                <button className="picker-search-clear" onClick={() => setSearchTerm("")}>
                  <CloseIcon style={{ fontSize: 14 }} />
                </button>
              )}
            </div>

            <div className="picker-filter-tabs">
              <button
                className={`picker-tab ${activeFilter === "all" ? "active" : ""}`}
                onClick={() => setActiveFilter("all")}
              >
                <PublicRoundedIcon style={{ fontSize: 15 }} /> All Ballots
              </button>
              <button
                className={`picker-tab ${activeFilter === "my" ? "active" : ""}`}
                onClick={() => setActiveFilter("my")}
              >
                <PersonOutlineRoundedIcon style={{ fontSize: 15 }} /> My Created
              </button>
            </div>

            {/* Custom Styled Sort Dropdown */}
            <div className="picker-sort-container" ref={sortRef}>
              <button
                type="button"
                className={`picker-sort-trigger ${sortOpen ? "open" : ""}`}
                onClick={() => setSortOpen((prev) => !prev)}
                title="Sort ballots"
              >
                <div className="picker-sort-trigger-left">
                  <SortRoundedIcon className="picker-sort-icon" />
                  <span className="picker-sort-label">
                    {SORT_OPTIONS.find((s) => s.value === sortBy)?.label || "Sort"}
                  </span>
                </div>
                <KeyboardArrowDownRoundedIcon className={`picker-sort-arrow ${sortOpen ? "rotated" : ""}`} />
              </button>

              <AnimatePresence>
                {sortOpen && (
                  <motion.div
                    className="picker-sort-menu"
                    initial={{ opacity: 0, y: -6, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -6, scale: 0.98 }}
                    transition={{ duration: 0.15, ease: "easeOut" }}
                  >
                    <div className="picker-sort-menu-header">
                      <span>Sort Ballots By</span>
                    </div>
                    <div className="picker-sort-menu-list">
                      {SORT_OPTIONS.map((opt) => {
                        const isSelected = sortBy === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            className={`picker-sort-item ${isSelected ? "selected" : ""}`}
                            onClick={() => {
                              setSortBy(opt.value);
                              setSortOpen(false);
                            }}
                          >
                            <div className="picker-sort-item-left">
                              <span className="picker-sort-item-icon">{opt.icon}</span>
                              <span className="picker-sort-item-text">{opt.label}</span>
                            </div>
                            {isSelected && (
                              <CheckRoundedIcon className="picker-sort-check" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* List Area */}
          <div className="picker-items-list">
            {loading ? (
              <div className="picker-loading-state">
                <CircularProgress size={32} style={{ color: "#48D1CC" }} />
                <p>Loading available ballots...</p>
              </div>
            ) : filteredBallots.length === 0 ? (
              <div className="picker-empty-state">
                <PollOutlinedIcon style={{ fontSize: 44, opacity: 0.3, color: "#48D1CC" }} />
                <h4>No ballots found</h4>
                <p>{searchTerm ? "Try refining your search keyword." : "No active ballots available to attach."}</p>
              </div>
            ) : (
              filteredBallots.map((ballot) => {
                const bId = ballot.voteId || ballot.id;
                const isSelected = selectedBallot && String(selectedBallot.voteId || selectedBallot.id) === String(bId);
                const isExpanded = expandedBallotIds.has(bId);
                const options = ballot.options || ballot.votingOption || [];
                const totalVotes = options.reduce((sum, opt) => sum + (Number(opt.voteCount) || 0), 0) || ballot.totalVotes || ballot.totalUserResponse || 0;
                const userSelectedOptionId = ballot.selectedOptionId || ballot.votedOptionId;

                return (
                  <div
                    key={bId}
                    className={`picker-card ballot-picker-card ${isSelected ? "selected" : ""} ${isExpanded ? "expanded" : ""}`}
                    onClick={() => handleCardClick(ballot)}
                    onDoubleClick={() => {
                      onSelectBallot(ballot);
                      onClose();
                    }}
                  >
                    <div className="picker-card-main-row">
                      <div className="picker-card-radio">
                        {isSelected ? (
                          <CheckCircleRoundedIcon style={{ color: "#48D1CC", fontSize: 22 }} />
                        ) : (
                          <RadioButtonUncheckedRoundedIcon style={{ color: "#4b635e", fontSize: 22 }} />
                        )}
                      </div>

                      <div className="picker-card-content">
                        <div className="picker-card-header-row">
                          <h4 className="picker-card-title">{ballot.title}</h4>
                          {ballot.haveSubBallot && (
                            <span className="picker-pill sub-ballot-pill" title="Has sub-ballots connected">
                              <AccountTreeRoundedIcon style={{ fontSize: 13, marginRight: 3, verticalAlign: "middle" }} />
                              Sub-Ballot
                            </span>
                          )}
                          {ballot.isVoted && (
                            <span className="picker-pill voted-status-pill" title="You have voted on this ballot">
                              <CheckRoundedIcon style={{ fontSize: 12, marginRight: 2 }} /> Voted
                            </span>
                          )}
                        </div>

                        {ballot.description && (
                          <p className="picker-card-desc">{ballot.description}</p>
                        )}

                        <div className="picker-card-meta-row">
                          {options.length > 0 && (
                            <button
                              type="button"
                              className={`btn-picker-options-toggle ${isExpanded ? "active" : ""}`}
                              onClick={(e) => toggleExpand(e, bId)}
                              title={isExpanded ? "Collapse options" : "Show all ballot options and results"}
                            >
                              <span>🗳️ <strong>{options.length}</strong> Options</span>
                              {isExpanded ? (
                                <KeyboardArrowUpRoundedIcon style={{ fontSize: 18 }} />
                              ) : (
                                <KeyboardArrowDownRoundedIcon style={{ fontSize: 18 }} />
                              )}
                            </button>
                          )}
                          <span className="picker-meta-item">
                            👥 <strong>{totalVotes}</strong> {totalVotes === 1 ? "Vote" : "Votes"}
                          </span>
                          {ballot.createdByUserName && (
                            <span className="picker-meta-item picker-creator-item">
                              {ballot.createdByUserPhoto ? (
                                <img
                                  src={ballot.createdByUserPhoto}
                                  alt={ballot.createdByUserName}
                                  className="picker-creator-avatar"
                                  onError={(e) => { e.target.style.display = "none"; }}
                                />
                              ) : (
                                <span>👤</span>
                              )}
                              <span>{ballot.createdByUserName}</span>
                            </span>
                          )}
                          {ballot.createdOn && (
                            <span className="picker-meta-item">
                              🕒 {new Date(ballot.createdOn).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="picker-card-action">
                        <button
                          type="button"
                          className={`btn-picker-select ${isSelected ? "selected" : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedBallot(ballot);
                          }}
                        >
                          {isSelected ? "Selected" : "Select"}
                        </button>
                      </div>
                    </div>

                    {/* Expandable Dropdown with all Ballot Options */}
                    <AnimatePresence>
                      {isExpanded && options.length > 0 && (
                        <motion.div
                          className="picker-card-options-dropdown"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.2, ease: "easeInOut" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="picker-options-dropdown-inner">
                            <div className="picker-options-dropdown-header">
                              <span>Options ({options.length})</span>
                              {totalVotes > 0 && <span className="picker-options-total-badge">{totalVotes} Total Votes</span>}
                            </div>
                            <div className="picker-options-grid">
                              {options.map((opt, optIdx) => {
                                const optId = opt.id || opt.optionId || optIdx;
                                const optText = opt.value || opt.optionText || opt.text || opt.option || opt.title || opt.name || (typeof opt === "string" ? opt : `Option ${optIdx + 1}`);
                                const optLetter = opt.optionLetter || opt.letter || String.fromCharCode(65 + optIdx);
                                const voteCount = Number(opt.voteCount ?? opt.count ?? 0);
                                const percentage = Number(opt.percentage ?? (totalVotes > 0 ? Math.round((voteCount / totalVotes) * 100) : 0));
                                const isUserPick = userSelectedOptionId && String(userSelectedOptionId).toLowerCase() === String(optId).toLowerCase();

                                return (
                                  <div
                                    key={optId}
                                    className={`picker-option-row ${isUserPick ? "picker-option-user-pick" : ""}`}
                                  >
                                    {percentage > 0 && (
                                      <div
                                        className="picker-opt-progress-bg"
                                        style={{ width: `${Math.min(percentage, 100)}%` }}
                                      />
                                    )}
                                    <span className="picker-opt-letter-badge">{optLetter}</span>
                                    <span className="picker-opt-text">
                                      {optText}
                                      {isUserPick && (
                                        <span className="picker-opt-user-tag">Your Vote</span>
                                      )}
                                    </span>
                                    <div className="picker-opt-stats-box">
                                      <span className="picker-opt-percent">{percentage}%</span>
                                      <span className="picker-opt-votes-pill">{voteCount} {voteCount === 1 ? "vote" : "votes"}</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })
            )}
          </div>

          {/* Sticky Footer Toolbar */}
          <div className="picker-modal-footer">
            <div className="picker-footer-selection-info">
              {selectedBallot ? (
                <div className="picker-selection-tag">
                  <HowToVoteRoundedIcon style={{ fontSize: 16 }} />
                  <span className="picker-selection-title">{selectedBallot.title}</span>
                  <button
                    type="button"
                    className="btn-picker-clear-sel"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedBallot(null);
                    }}
                    title="Deselect"
                  >
                    <CloseIcon style={{ fontSize: 14 }} />
                  </button>
                </div>
              ) : (
                <span className="picker-none-selected">Select a ballot above to attach</span>
              )}
            </div>

            <div className="picker-footer-actions">
              <button type="button" className="btn-picker-cancel" onClick={onClose}>
                Cancel
              </button>
              <button
                type="button"
                className="btn-picker-confirm"
                disabled={!selectedBallot}
                onClick={handleConfirmSelection}
              >
                <CheckCircleRoundedIcon style={{ fontSize: 17 }} />
                Attach Ballot
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  );
}

